import React from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../contexts/LanguageContext';
import { usePermissions } from '../../../hooks/usePermissions';

const PackageDefinitionsForm = (props) => {
    const {
        dialogVisible,
        editMode,
        submitted,
        hideDialog,
        renderDialogFooter,
        packageName,
        description,
        group,
        status,
        parts,
        labors,
        setPackageName,
        setDescription,
        setGroup,
        setStatus,
        partOptions,
        selectedPartCode,
        setSelectedPartCode,
        quantity,
        setQuantity,
        addPart,
        removePart,
        isEditable = true,
        laborOptions,
        selectedLaborCode,
        setSelectedLaborCode,
        laborTime,
        setLaborTime,
        addLabor,
        removeLabor,
        // Parça arama için eklenen props
        fetchingParts,
        partFilterValue,
        handlePartFilterChange
    } = props;

    const { th } = useLanguage();
    const { hasWarrantyTeamRole } = usePermissions();
    const isWarrantyTeam = hasWarrantyTeamRole();

    const groupOptions = [
        { label: th('packageDefinitions.groupWholesale'), value: 1 },
        { label: th('packageDefinitions.groupSalesOrder'), value: 2 },
        { label: th('packageDefinitions.groupRepairExit'), value: 3 },
        ...(!isEditable || isWarrantyTeam ? [{ label: th('packageDefinitions.groupVehiclePeriodic'), value: 4 }] : [])
    ];
    const partItemTemplate = (opt) => (
        <div>
            <strong>{opt.code}</strong> – {opt.name}
        </div>
    );

    // Loading durumunda gösterilecek template
    const loadingTemplate = () => (
        <div className="flex align-items-center justify-content-center p-3">
            <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
            <span className="ml-2">{th('dealerPartsReturnProcess.returnRequest.searching')}</span>
        </div>
    );

    // Custom filter template - Dropdown'ın internal filter'ı yerine kendi kontrollü input'umuz
    const partFilterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText value={partFilterValue} onChange={(e) => handlePartFilterChange(e.target.value)} className="p-dropdown-filter w-full" placeholder={th('dealerPartsReturnProcess.returnRequest.partSearchPlaceholder')} autoFocus />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    const laborItemTemplate = (opt) => (
        <div>
            <strong>{opt.code}</strong> – {opt.name}
        </div>
    );

    const valueTemplate = (code) => {
        const opt = partOptions?.find((o) => o.code === code);
        return opt ? (
            <span>
                {opt.code} – {opt.name}
            </span>
        ) : (
            <span>-</span>
        );
    };

    const laborTemplate = (row) =>
        Array.isArray(row.labors) && row.labors.length ? row.labors.map((l) => <Tag key={`${row.partCode}-${l.laborCode}`} value={`${l.laborCode}${l?.laborName ? ' - ' + l.laborName : ''}`} className="mr-2 mb-2" />) : '-';

    const actionTemplate = (_, { rowIndex }) => {
        if (!isEditable) return null;
        return <Button icon="pi pi-trash" rounded severity="danger" text onClick={() => removePart(rowIndex)} />;
    };

    const laborActionTemplate = (_, { rowIndex }) => {
        if (!isEditable) return null;
        return <Button icon="pi pi-trash" rounded severity="danger" text onClick={() => removeLabor(rowIndex)} />;
    };

    return (
        <Dialog visible={dialogVisible} style={{ width: 920 }} header={editMode ? th('packageDefinitions.edit') : th('packageDefinitions.new')} modal className="p-fluid" footer={renderDialogFooter ? renderDialogFooter() : null} onHide={hideDialog}>
            <div className="mb-1">
                <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                    <div className="flex align-items-center">
                        <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                            {th('packageDefinitions.generalDefinitions')}
                        </h6>
                    </div>
                </div>
                <div className="card-body" style={{ padding: '1rem' }}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field mb-1">
                                <label className="font-bold mb-2 block">{th('packageDefinitions.packageName')}</label>
                                <InputText value={packageName} onChange={(e) => setPackageName(e.target.value)} className={submitted && !packageName?.trim() ? 'p-invalid' : ''} readOnly={!isEditable} disabled={!isEditable} />
                                {submitted && !packageName?.trim() && <small className="p-error">{th('packageDefinitions.validation.nameRequired')}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field mb-1">
                                <label className="font-bold mb-2 block">{th('packageDefinitions.group')}</label>
                                <Dropdown
                                    value={group}
                                    onChange={(e) => setGroup(e.value)}
                                    options={groupOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={th('packageDefinitions.groupPlaceholder')}
                                    className={submitted && !group ? 'p-invalid' : ''}
                                    disabled={!isEditable}
                                />
                                {submitted && !group && <small className="p-error">{th('packageDefinitions.validation.groupRequired')}</small>}
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field mb-1">
                                <label className="font-bold mb-2 block">{th('packageDefinitions.description')}</label>
                                <InputTextarea autoResize rows={3} value={description} onChange={(e) => setDescription(e.target.value)} readOnly={!isEditable} disabled={!isEditable} />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field-checkbox mb-1">
                                <Checkbox inputId="status" checked={status === 1} onChange={(e) => setStatus(e.checked ? 1 : 0)} disabled={!isEditable} />
                                <label htmlFor="status" className="ml-2">
                                    {th('common.active')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isEditable && (
                <div className="mb-1">
                    <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                        <div className="flex align-items-center">
                            <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                {th('packageDefinitions.partDefinitions')}
                            </h6>
                        </div>
                    </div>

                    <div className="card-body" style={{ padding: '1rem' }}>
                        <div className="grid align-items-end">
                            <div className="col-12 md:col-7">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">{th('packageDefinitions.partName')}</label>
                                    <Dropdown
                                        value={selectedPartCode}
                                        onChange={(e) => setSelectedPartCode(e.value)}
                                        options={partOptions}
                                        optionLabel="label"
                                        optionValue="code"
                                        filter
                                        filterTemplate={partFilterTemplate}
                                        showClear
                                        placeholder={th('packageDefinitions.partName')}
                                        itemTemplate={partItemTemplate}
                                        virtualScrollerOptions={{ itemSize: 40 }}
                                        className="w-full"
                                        loading={fetchingParts}
                                        emptyFilterMessage={fetchingParts ? loadingTemplate() : th('dealerPartsReturnProcess.returnRequest.noResults')}
                                        emptyMessage={fetchingParts ? loadingTemplate() : th('common.noRecords')}
                                    />
                                </div>
                            </div>

                            <div className="col-8 md:col-3">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">{th('packageDefinitions.quantity')}</label>
                                    <InputNumber value={quantity} onValueChange={(e) => setQuantity(e.value)} min={1} />
                                </div>
                            </div>

                            <div className="col-4 md:col-2">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">&nbsp;</label>
                                    <Button label={th('common.add') || 'Ekle'} icon="pi pi-plus" className="w-full md:w-auto" style={{ padding: '10px' }} onClick={addPart} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-1">
                <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                    <div className="flex align-items-center">
                        <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                            {th('packageDefinitions.addedParts')}
                        </h6>
                    </div>
                </div>

                <div className="card-body" style={{ padding: '0.5rem' }}>
                    <DataTable value={parts} dataKey="partCode" className="mt-3" responsiveLayout="scroll" size="small" emptyMessage="-">
                        <Column field="partCode" header={th('packageDefinitions.partCode')} style={{ width: '12rem' }} />
                        <Column field="partName" header={th('packageDefinitions.partName')} />
                        <Column field="quantity" header={th('packageDefinitions.quantity')} style={{ width: '8rem' }} />
                        <Column header={th('packageDefinitions.labor')} body={laborTemplate} />
                        {isEditable && <Column header="" body={actionTemplate} style={{ width: '5rem' }} />}
                    </DataTable>
                </div>
            </div>

            {isEditable && (
                <div className="mb-1">
                    <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                        <div className="flex align-items-center">
                            <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                {th('packageDefinitions.laborDefinitions')}
                            </h6>
                        </div>
                    </div>

                    <div className="card-body" style={{ padding: '1rem' }}>
                        <div className="grid align-items-end">
                            <div className="col-12 md:col-7">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">{th('packageDefinitions.laborName')}</label>
                                    <Dropdown
                                        value={selectedLaborCode}
                                        onChange={(e) => setSelectedLaborCode(e.value)}
                                        options={laborOptions}
                                        optionLabel="label"
                                        optionValue="code"
                                        filter
                                        filterBy="label"
                                        showClear
                                        placeholder={th('packageDefinitions.laborName')}
                                        itemTemplate={laborItemTemplate}
                                        virtualScrollerOptions={{ itemSize: 40 }}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="col-8 md:col-3">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">{th('packageDefinitions.laborTime')}</label>
                                    <InputNumber value={laborTime} onValueChange={(e) => setLaborTime(e.value)} min={0.01} minFractionDigits={0} maxFractionDigits={2} />
                                </div>
                            </div>

                            <div className="col-4 md:col-2">
                                <div className="field mb-2">
                                    <label className="font-medium mb-1 block">&nbsp;</label>
                                    <Button label={th('common.add') || 'Ekle'} icon="pi pi-plus" className="w-full md:w-auto" style={{ padding: '10px' }} onClick={addLabor} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-1">
                <div className="card-header" style={{ background: 'rgb(39, 58, 129)', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                    <div className="flex align-items-center">
                        <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                            {th('packageDefinitions.addedLabors')}
                        </h6>
                    </div>
                </div>

                <div className="card-body" style={{ padding: '0.5rem' }}>
                    <DataTable value={labors} dataKey="laborCode" className="mt-3" responsiveLayout="scroll" size="small" emptyMessage="-">
                        <Column field="laborCode" header={th('packageDefinitions.laborCode')} style={{ width: '12rem' }} />
                        <Column field="laborName" header={th('packageDefinitions.laborName')} />
                        <Column field="laborTime" header={th('packageDefinitions.laborTime')} style={{ width: '8rem' }} />
                        {isEditable && <Column header="" body={laborActionTemplate} style={{ width: '5rem' }} />}
                    </DataTable>
                </div>
            </div>
        </Dialog>
    );
};

export default PackageDefinitionsForm;
